import { z } from 'zod'

import { ModuleTypeEnum } from './module-type'

// Modules that route record-level access through the new record-permissions
// system. Membership tables (project_members, entity_members) live alongside —
// member rows own per-record metadata while record_permission_grants enforce
// the access decisions.
export const RECORD_PERMISSION_MODULES = [
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.EVENT,
  ModuleTypeEnum.INVENTORY,
  ModuleTypeEnum.NOTE,
  ModuleTypeEnum.SUPPLIER,
  ModuleTypeEnum.TEAM,
] as const

export type RecordPermissionModule = (typeof RECORD_PERMISSION_MODULES)[number]

export enum RecordPermissionPrincipalTypeEnum {
  USER = 'USER',
  MEMBER = 'MEMBER',
  ORG_ROLE = 'ORG_ROLE',
  PROJECT_ROLE = 'PROJECT_ROLE',
  MODULE_ROLE = 'MODULE_ROLE',
  RECORD_OWNER = 'RECORD_OWNER',
  ASSIGNEE = 'ASSIGNEE',
}

export enum RecordPermissionInheritanceModeEnum {
  NONE = 'NONE',
  PARENT = 'PARENT',
  PROJECT = 'PROJECT',
}

export const RecordPermissionCapabilitySchema = z.string().min(1)
export type RecordPermissionCapability = z.infer<
  typeof RecordPermissionCapabilitySchema
>

export interface DefaultRecordPermissionRole {
  moduleType: RecordPermissionModule
  key: string
  name: string
  description: string
  allowCapabilities: string[]
  denyCapabilities?: string[]
}

const cap = (module: string, value: string) => `${module}.${value}`
const field = (module: string, name: string, action = 'read') =>
  `${module}.field.${name}.${action}`

export const RECORD_PERMISSION_SENSITIVE_FIELD_CAPABILITIES: Partial<
  Record<ModuleTypeEnum, string[]>
> = {
  [ModuleTypeEnum.PROJECT]: [
    field('project', 'budget'),
    field('project', 'cost'),
    field('project', 'billRate'),
    field('project', 'payRate'),
    field('project', 'margin'),
    field('project', 'profitability'),
  ],
  [ModuleTypeEnum.DEAL]: [field('deal', 'amount'), field('deal', 'forecast')],
  [ModuleTypeEnum.TEAM]: [
    field('team', 'compensation'),
    field('team', 'payRate'),
    field('team', 'costRate'),
    field('team', 'utilizationCost'),
  ],
  [ModuleTypeEnum.ENTITY]: [
    field('entity', 'budget'),
    field('entity', 'rateCard'),
    field('entity', 'revenue'),
    field('entity', 'margin'),
  ],
  [ModuleTypeEnum.SUPPLIER]: [
    field('supplier', 'pricing'),
    field('supplier', 'spend'),
    field('supplier', 'terms'),
  ],
}

const allRecord = (module: string) => [
  cap(module, 'read'),
  cap(module, 'create'),
  cap(module, 'update'),
  cap(module, 'delete'),
  cap(module, 'manage'),
]

const readOnly = (module: string) => [cap(module, 'read')]
const contributor = (module: string) => [
  cap(module, 'read'),
  cap(module, 'update'),
]

export const DEFAULT_RECORD_PERMISSION_ROLES: DefaultRecordPermissionRole[] = [
  {
    moduleType: ModuleTypeEnum.ENTITY,
    key: 'entity_admin',
    name: 'Entity Admin',
    description:
      'Full entity access including settings, financial fields, members, and all tabs.',
    allowCapabilities: [
      ...allRecord('entity'),
      'entity.feature.manageAccess',
      'entity.feature.manageMembers',
      'entity.tab.contacts',
      'entity.tab.projects',
      'entity.tab.documents',
      'entity.tab.discussions',
      'entity.tab.notes',
      'entity.tab.financials',
      'entity.tab.contracts_billing',
      'entity.tab.staffing',
      'entity.tab.client_portal',
    ],
  },
  {
    moduleType: ModuleTypeEnum.ENTITY,
    key: 'account_manager',
    name: 'Account Manager',
    description:
      'Manage entity details, members, related projects, deals, and contacts. No financials by default.',
    allowCapabilities: [
      ...contributor('entity'),
      'entity.feature.manageRelationships',
      'entity.feature.manageMembers',
      'entity.tab.contacts',
      'entity.tab.projects',
      'entity.tab.documents',
      'entity.tab.discussions',
      'entity.tab.notes',
      'entity.tab.staffing',
    ],
  },
  {
    moduleType: ModuleTypeEnum.ENTITY,
    key: 'account_contributor',
    name: 'Account Contributor',
    description:
      'Update non-sensitive entity data, contribute to related projects, contacts, and discussions.',
    allowCapabilities: [
      ...contributor('entity'),
      'entity.tab.contacts',
      'entity.tab.projects',
      'entity.tab.documents',
      'entity.tab.discussions',
      'entity.tab.notes',
    ],
  },
  {
    moduleType: ModuleTypeEnum.ENTITY,
    key: 'financial_viewer',
    name: 'Financial Viewer',
    description:
      'Read entity financial fields, budgets, and rate-card references.',
    allowCapabilities: [
      ...readOnly('entity'),
      ...(RECORD_PERMISSION_SENSITIVE_FIELD_CAPABILITIES[
        ModuleTypeEnum.ENTITY
      ] ?? []),
      'entity.tab.financials',
      'entity.tab.contracts_billing',
    ],
  },
  {
    moduleType: ModuleTypeEnum.ENTITY,
    key: 'read_only',
    name: 'Read Only',
    description:
      'Read overview only — no related records, financials, or settings.',
    allowCapabilities: readOnly('entity'),
  },

  {
    moduleType: ModuleTypeEnum.PROJECT,
    key: 'project_admin',
    name: 'Project Admin',
    description:
      'Full project access including settings, members, financials, and custom fields.',
    allowCapabilities: [
      ...allRecord('project'),
      'project.feature.manageMembers',
      'project.tab.tasks',
      'project.tab.files',
      'project.tab.discussions',
      'project.tab.notes',
      'project.tab.time',
      'project.tab.financials',
    ],
  },
  {
    moduleType: ModuleTypeEnum.PROJECT,
    key: 'project_manager',
    name: 'Project Manager',
    description:
      'Manage project work, team, tabs, timeline, files, discussions, and notes.',
    allowCapabilities: [
      ...contributor('project'),
      'project.feature.manageMembers',
      'project.tab.tasks',
      'project.tab.files',
      'project.tab.discussions',
      'project.tab.notes',
    ],
  },
  {
    moduleType: ModuleTypeEnum.PROJECT,
    key: 'lead',
    name: 'Lead',
    description:
      'Coordinate work execution without sensitive financial access by default.',
    allowCapabilities: [
      ...contributor('project'),
      'project.tab.tasks',
      'project.tab.time',
    ],
  },
  {
    moduleType: ModuleTypeEnum.PROJECT,
    key: 'contributor',
    name: 'Contributor',
    description:
      'Participate in project work, files, discussions, notes, and own time.',
    allowCapabilities: [
      'project.read',
      'project.tab.tasks',
      'project.tab.files',
      'project.tab.discussions',
      'project.tab.notes',
    ],
  },
  {
    moduleType: ModuleTypeEnum.PROJECT,
    key: 'reviewer',
    name: 'Reviewer',
    description:
      'Read, review, and comment without settings or financial access.',
    allowCapabilities: [
      'project.read',
      'project.tab.tasks',
      'project.tab.files',
      'project.tab.discussions',
    ],
  },
  {
    moduleType: ModuleTypeEnum.PROJECT,
    key: 'stakeholder',
    name: 'Stakeholder',
    description:
      'Read project status, milestones, approved files, and approved notes.',
    allowCapabilities: [
      'project.read',
      'project.tab.files',
      'project.tab.notes',
    ],
  },
  {
    moduleType: ModuleTypeEnum.PROJECT,
    key: 'read_only',
    name: 'Read Only',
    description: 'Minimal project read access.',
    allowCapabilities: readOnly('project'),
  },

  {
    moduleType: ModuleTypeEnum.EVENT,
    key: 'event_admin',
    name: 'Event Admin',
    description:
      'Full event access including setup, attendees, sessions, agenda, and all tabs.',
    allowCapabilities: [
      ...allRecord('event'),
      'event.feature.manageAttendees',
      'event.feature.manageSessions',
      'event.feature.manageAgenda',
      'event.tab.overview',
      'event.tab.sessions',
      'event.tab.agenda',
      'event.tab.attendees',
      'event.tab.files',
      'event.tab.discussions',
      'event.tab.notes',
    ],
  },
  {
    moduleType: ModuleTypeEnum.EVENT,
    key: 'event_manager',
    name: 'Event Manager',
    description:
      'Manage event details, sessions, agenda, attendees, and collaboration tabs.',
    allowCapabilities: [
      ...contributor('event'),
      'event.feature.manageAttendees',
      'event.feature.manageSessions',
      'event.feature.manageAgenda',
      'event.tab.overview',
      'event.tab.sessions',
      'event.tab.agenda',
      'event.tab.attendees',
      'event.tab.files',
      'event.tab.discussions',
      'event.tab.notes',
    ],
  },
  {
    moduleType: ModuleTypeEnum.EVENT,
    key: 'host',
    name: 'Host',
    description: 'Coordinate sessions and attendee-facing event activity.',
    allowCapabilities: [
      ...contributor('event'),
      'event.feature.manageSessions',
      'event.tab.overview',
      'event.tab.sessions',
      'event.tab.agenda',
      'event.tab.attendees',
      'event.tab.discussions',
    ],
  },
  {
    moduleType: ModuleTypeEnum.EVENT,
    key: 'speaker',
    name: 'Speaker',
    description:
      'View event schedule, session details, files, and discussions.',
    allowCapabilities: [
      ...readOnly('event'),
      'event.tab.overview',
      'event.tab.sessions',
      'event.tab.agenda',
      'event.tab.files',
      'event.tab.discussions',
    ],
  },
  {
    moduleType: ModuleTypeEnum.EVENT,
    key: 'attendee',
    name: 'Attendee',
    description: 'View attendee-facing event overview, agenda, and sessions.',
    allowCapabilities: [
      ...readOnly('event'),
      'event.tab.overview',
      'event.tab.sessions',
      'event.tab.agenda',
    ],
  },
  {
    moduleType: ModuleTypeEnum.EVENT,
    key: 'read_only',
    name: 'Read Only',
    description: 'Minimal event read access.',
    allowCapabilities: readOnly('event'),
  },

  // ENTITY, PROJECT, and EVENT roles are defined explicitly above with their
  // tab/feature capabilities. Modules below use generated role templates.
  ...(
    [
      {
        moduleType: ModuleTypeEnum.DEAL,
        moduleKey: 'deal',
        names: [
          'Deal Admin',
          'Deal Owner',
          'Sales Contributor',
          'Financial Viewer',
          'Read Only',
        ],
      },
      {
        moduleType: ModuleTypeEnum.NOTE,
        moduleKey: 'note',
        names: ['Note Admin', 'Editor', 'Commenter', 'Viewer'],
      },
      {
        moduleType: ModuleTypeEnum.INVENTORY,
        moduleKey: 'inventory',
        names: [
          'Inventory Admin',
          'Stock Manager',
          'Finance/Cost Viewer',
          'Read Only',
        ],
      },
      {
        moduleType: ModuleTypeEnum.SUPPLIER,
        moduleKey: 'supplier',
        names: [
          'Supplier Admin',
          'Buyer',
          'Finance/Spend Viewer',
          'Read Only',
        ],
      },
      {
        moduleType: ModuleTypeEnum.TEAM,
        moduleKey: 'team',
        names: [
          'People Admin',
          'Manager',
          'Finance/Comp Viewer',
          'Profile Editor',
          'Read Only',
        ],
      },
    ] satisfies Array<{
      moduleType: RecordPermissionModule
      moduleKey: string
      names: string[]
    }>
  ).flatMap(({ moduleType, moduleKey, names }) =>
    names.map((name, index) => {
      const key = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
      const isAdmin = index === 0
      const isReadOnly = name === 'Read Only' || name === 'Viewer'
      const isFinancial = name.includes('Financial') || name.includes('Finance')
      return {
        moduleType,
        key,
        name,
        description: `${name} access for ${moduleKey} records.`,
        allowCapabilities: isAdmin
          ? allRecord(moduleKey)
          : isReadOnly
            ? readOnly(moduleKey)
            : [
                ...(isFinancial ? readOnly(moduleKey) : contributor(moduleKey)),
                ...(isFinancial
                  ? (RECORD_PERMISSION_SENSITIVE_FIELD_CAPABILITIES[
                      moduleType
                    ] ?? [])
                  : []),
              ],
      }
    }),
  ),
]

export const RecordPermissionRoleSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  allowCapabilities: z.array(z.string()).default([]),
  denyCapabilities: z.array(z.string()).default([]),
  isSystem: z.boolean().default(false),
  isActive: z.boolean().default(true),
})

export type RecordPermissionRole = z.infer<typeof RecordPermissionRoleSchema>

export const CreateRecordPermissionRoleDtoSchema =
  RecordPermissionRoleSchema.omit({
    id: true,
    organizationId: true,
    isSystem: true,
    isActive: true,
  }).extend({
    key: z.string().min(1).optional(),
    isActive: z.boolean().optional(),
  })

export type CreateRecordPermissionRoleDto = z.infer<
  typeof CreateRecordPermissionRoleDtoSchema
>

export const UpdateRecordPermissionRoleDtoSchema =
  CreateRecordPermissionRoleDtoSchema.partial().extend({
    isActive: z.boolean().optional(),
  })

export type UpdateRecordPermissionRoleDto = z.infer<
  typeof UpdateRecordPermissionRoleDtoSchema
>

export const RecordPermissionGrantSchema = z.object({
  id: z.string().uuid().optional(),
  roleId: z.string().uuid().nullable().optional(),
  principalType: z.nativeEnum(RecordPermissionPrincipalTypeEnum),
  principalId: z.string().uuid().nullable().optional(),
  principalKey: z.string().nullable().optional(),
  sourceModuleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  allowCapabilities: z.array(z.string()).default([]),
  denyCapabilities: z.array(z.string()).default([]),
  isActive: z.boolean().default(true),
})

export type RecordPermissionGrant = z.infer<typeof RecordPermissionGrantSchema>

export const RecordAccessPolicySchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  recordId: z.string().uuid().nullable().optional(),
  inheritanceMode: z
    .nativeEnum(RecordPermissionInheritanceModeEnum)
    .default(RecordPermissionInheritanceModeEnum.NONE),
  parentModuleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  parentRecordId: z.string().uuid().nullable().optional(),
  settings: z.record(z.any()).default({}),
  grants: z.array(RecordPermissionGrantSchema).default([]),
})

export type RecordAccessPolicy = z.infer<typeof RecordAccessPolicySchema>

export const UpdateRecordAccessPolicyDtoSchema = z.object({
  inheritanceMode: z.nativeEnum(RecordPermissionInheritanceModeEnum).optional(),
  parentModuleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  parentRecordId: z.string().uuid().nullable().optional(),
  settings: z.record(z.any()).optional(),
})

export type UpdateRecordAccessPolicyDto = z.infer<
  typeof UpdateRecordAccessPolicyDtoSchema
>

export const CreateRecordPermissionGrantDtoSchema =
  RecordPermissionGrantSchema.omit({ id: true, isActive: true }).extend({
    isActive: z.boolean().optional(),
  })

export type CreateRecordPermissionGrantDto = z.infer<
  typeof CreateRecordPermissionGrantDtoSchema
>

export const UpdateRecordPermissionGrantDtoSchema =
  CreateRecordPermissionGrantDtoSchema.partial()

export type UpdateRecordPermissionGrantDto = z.infer<
  typeof UpdateRecordPermissionGrantDtoSchema
>

export const AssignRecordPermissionRoleDtoSchema = z.object({
  roleId: z.string().uuid(),
  principalType: z.nativeEnum(RecordPermissionPrincipalTypeEnum),
  principalId: z.string().uuid().nullable().optional(),
  principalKey: z.string().nullable().optional(),
  sourceModuleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  allowCapabilities: z.array(z.string()).optional(),
  denyCapabilities: z.array(z.string()).optional(),
})

export type AssignRecordPermissionRoleDto = z.infer<
  typeof AssignRecordPermissionRoleDtoSchema
>

export interface EffectiveRecordPermissions {
  moduleType: ModuleTypeEnum
  recordId: string | null
  hasPolicy: boolean
  bypass: boolean
  allowCapabilities: string[]
  denyCapabilities: string[]
  canRead: boolean
  canUpdate: boolean
  canManage: boolean
}

export function getCustomFieldCapability(
  moduleType: ModuleTypeEnum,
  fieldId: string,
  action: 'read' | 'update' | 'manage',
): string {
  return `${moduleType.toLowerCase()}.customField.${fieldId}.${action}`
}
